import { ReactElement } from 'react';

export interface ProducerNavItem {
  label: string;
  href: string;
  icon?: ReactElement;
}

export interface ProducerNavSection {
  title?: string;
  items: ProducerNavItem[];
}

export interface ProducerNavModule {
  id: string;
  label: string;
  /**
   * What the 64px desktop rail shows instead of `label`.
   *
   * The rail is a single narrow column, so anything past about eight
   * characters wraps to two lines and the icons stop sitting on a rhythm —
   * "Request a Job" and "Your Animals" both broke. The drawer is full width
   * and keeps the long form, which is the one that reads properly in a list.
   */
  shortLabel?: string;
  icon: ReactElement;
  /** Navigation target. If omitted and `action` is "callback", the shell triggers the registered handler instead. */
  href?: string;
  /** Marks this module as an action that should fire a side-channel handler (e.g. open a modal) rather than navigate. */
  action?: 'callback';
  sections?: ProducerNavSection[];
  /**
   * Optional badge count shown on the nav icon (e.g. cart item count).
   * A value of 0 hides the badge (MUI Badge invisible at 0).
   */
  badgeCount?: number;
  /**
   * Marks the module as in-beta. Renders a small "Beta" chip on the nav item,
   * mirroring the processor "Cut Sheets" nav badge.
   */
  beta?: boolean;
}

// Icons are injected at render time to avoid importing MUI icons here.
// The config is built by the hook/component that consumes it.
// This file provides the types and a factory function.

export function buildProducerNavConfig(
  icons: {
    requestJob: ReactElement;
    dashboard: ReactElement;
    jobs: ReactElement;
    animals: ReactElement;
    chat: ReactElement;
    orders: ReactElement;
    processors: ReactElement;
    scheduleJob: ReactElement;
    find: ReactElement;
    available: ReactElement;
    transactions: ReactElement;
    cart: ReactElement;
    customers: ReactElement;
  },
  options?: {
    buyerOnly?: boolean;
    hasProcessors?: boolean;
    /** Outstanding actions, badged on the Tasks entry. */
    taskCount?: number;
    /**
     * Total unread chat count for the signed-in user (direct + processor
     * rooms), sourced from `navBadgeCounts.unreadChats` — the same
     * loader-backed definition the processor top-nav badge uses. Rendered on
     * the Chat module's badge; a falsy value (0/undefined) hides it (see
     * IconRail's `invisible={!mod.badgeCount}`).
     */
    chatUnreadCount?: number;
    /**
     * Orders a plant has taken for them (wholesale or retail), when they have
     * any: shows "Plant orders", badged with the recurring orders waiting on
     * their OK. Unset hides it.
     */
    plantOrders?: { waiting: number } | null;
  },
): ProducerNavModule[] {
  const plantOrders: ProducerNavModule[] = options?.plantOrders
    ? [
        {
          id: 'plantOrders',
          label: 'Plant orders',
          shortLabel: 'Plant',
          icon: icons.transactions,
          href: '/my-orders',
          badgeCount: options.plantOrders.waiting,
        },
      ]
    : [];
  if (options?.buyerOnly) {
    // Ranchers item added by the ranchers-page track; find/available/transactions
    // retired 2026-07-09 (routes redirect, files preserved). Explore was
    // re-added the same day as a list-style "farms + pickup near me" page
    // (the old map-based /explore redirect for buyers was reversed).
    return [
      // The Marketplace tab and the cart were removed when the buyer
      // marketplace was switched off (PRO-784).
      {
        // The dashboard, which for a buyer IS their orders — opened on the
        // Your Orders tab the label names; the badge counts the cut sheets
        // owed there. The separate `/buyer/orders` list it replaced
        // redirects to the same tab.
        id: 'orders',
        label: 'Your Orders',
        shortLabel: 'Orders',
        icon: icons.orders,
        href: '/dashboard?tab=orders',
        badgeCount: options?.taskCount,
      },
      ...plantOrders,
      {
        // Restored 2026-07-12: the P4 4-item nav dropped Chat for buyers,
        // but the shared /chat messaging page stayed live (reachable by URL).
        id: 'chat',
        label: 'Chat',
        icon: icons.chat,
        href: '/chat',
        badgeCount: options?.chatUnreadCount,
      },
    ];
  }

  return [
    {
      id: 'requestJob',
      label: 'Request a Job',
      shortLabel: 'Request',
      icon: icons.requestJob,
      ...(!options?.hasProcessors
        ? { href: '/marketplace?mode=process' }
        : { action: 'callback' as const }),
    },
    {
      // The dashboard is the TASK list now — what needs doing — and the job
      // archive moved to its own searchable page. Two entries because they
      // answer different questions: "what do I owe anyone?" and "where is
      // that job from March?".
      id: 'dashboard',
      label: 'Tasks',
      icon: icons.dashboard,
      href: '/dashboard',
      badgeCount: options?.taskCount,
    },
    {
      id: 'jobs',
      label: 'Your Jobs',
      shortLabel: 'Jobs',
      icon: icons.jobs,
      href: '/jobs',
    },
    ...plantOrders,
    {
      id: 'myHerd',
      // "Your", not "My" — the app speaks to the producer in the second
      // person everywhere else ("Your processor needs these"), and this was
      // the one label that didn't.
      label: 'Your Animals',
      shortLabel: 'Animals',
      icon: icons.animals,
      href: '/my-animals',
      beta: true,
    },
    {
      // Producer IA reshape 2026-07-10: replaces the retired "Selling" module
      // (and its Setup / Buyer Roster flyout). Selling setup now lives on
      // /profile; the roster became the person-centric /customers page.
      id: 'customers',
      label: 'Customers',
      icon: icons.customers,
      href: '/customers',
      beta: true,
    },
    ...(options?.hasProcessors
      ? [
          {
            id: 'processors',
            label: 'Processor',
            icon: icons.processors,
            href: '/my-processor',
          },
        ]
      : []),
    {
      id: 'chat',
      label: 'Chat',
      icon: icons.chat,
      href: '/chat',
      badgeCount: options?.chatUnreadCount,
    },
  ];
}
